import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { DealCard } from "./DealCard";

interface Stage {
  id: string;
  name: string;
  color: string | null;
}

interface Deal {
  id: string;
  title: string;
  value: number | null;
  expected_close_date: string | null;
  contact?: {
    name: string;
    organization: string | null;
  };
}

interface PipelineStageProps {
  stage: Stage;
  deals: Deal[];
  onAddDeal: () => void;
  onEditDeal: (deal: Deal) => void;
}

export function PipelineStage({ stage, deals, onAddDeal, onEditDeal }: PipelineStageProps) {
  const { setNodeRef } = useDroppable({
    id: stage.id,
  });

  const totalValue = deals.reduce((sum, deal) => sum + (deal.value || 0), 0);

  return (
    <div className="flex-shrink-0 w-64">
      <Card className="p-3 h-full flex flex-col">
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1.5">
              {stage.color && (
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: stage.color }}
                />
              )}
              <h3 className="font-semibold text-sm">{stage.name}</h3>
            </div>
            <span className="text-xs text-muted-foreground">
              {deals.length}
            </span>
          </div>
          <div className="text-xs text-muted-foreground">
            ${totalValue.toLocaleString()}
          </div>
        </div>

        <div
          ref={setNodeRef}
          className="flex-1 space-y-2 min-h-[200px] overflow-y-auto"
        >
          <SortableContext
            items={deals.map(d => d.id)}
            strategy={verticalListSortingStrategy}
          >
            {deals.map((deal) => (
              <DealCard
                key={deal.id}
                deal={deal}
                onClick={() => onEditDeal(deal)}
              />
            ))}
          </SortableContext>
        </div>

        <Button
          variant="ghost"
          size="sm"
          className="w-full mt-3 text-xs h-8"
          onClick={onAddDeal}
        >
          <Plus className="h-3 w-3 mr-1" />
          Add Deal
        </Button>
      </Card>
    </div>
  );
}
